import { MainPageContainer, UpperInfoContainer } from 'components/PageLayout';
import { convertToDate, useZurichCSIReport } from 'pages/hooks';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dateToString, formatDate } from 'utils/helpers';
import { AxiosError } from 'axios';
import { createReportURL } from 'core/routes';
import { errorMessages } from 'api/resources/responseMessages/errorMessages';
import { ReportGridContainer } from 'components/ReportGridContainer';
import { NoReportDataMessage } from 'components/ReportsComponents/NoReportDataMessage';
import { ReportBackButton } from 'components/ReportsComponents/ReportBackButton';
import { ReportHeader } from 'components/ReportsComponents/ReportHeader';
import {
  columns,
  ZURICH_CSI_REPORT,
  ZURICH_CSI_REPORT_GRID_TITLE,
  ZURICH_CSI_REPORT_MESSAGE,
} from './constants';
import { ReportSearchForm } from 'components/ReportForms/ReportSearchForm';
import { ReportProps } from 'pages/ReportsDetails/hooks';
import { reportLogger } from 'pages/ReportsDetails/reportLogger';
import ReportGridNew from 'components/ReportGrid_New';
import { Row } from '@devexpress/dx-react-grid';

// TODO: remove after testing
const mockedReportData: Row[] = [
  {
    registration: 'ABC123',
    claimReference: 'CR789',
    manufacturer: 'Toyota',
    model: 'Camry',
    ownerName: 'John Doe',
    ownerEmail: 'johndoe@example.com',
    ownerMobile: '123-456-7890',
    dateReturned: '2000-01-01T00:00:00',
    elapsedK2K: 5,
    name: 'Jane Smith',
    comments: 'Vehicle repaired and returned to owner.',
  },
  {
    registration: 'DEF456',
    claimReference: 'CR790',
    manufacturer: 'Honda',
    model: 'Civic',
    ownerName: 'Alice Johnson',
    ownerEmail: 'alicejohnson@example.com',
    ownerMobile: '234-567-8901',
    dateReturned: '2001-02-15T10:30:00',
    elapsedK2K: 7,
    name: 'Bob Brown',
    comments: 'Routine maintenance completed.'
  },
  {
    registration: 'GHI789',
    claimReference: 'CR791',
    manufacturer: 'Ford',
    model: 'Focus',
    ownerName: 'Michael Green',
    ownerEmail: 'michaelgreen@example.com',
    ownerMobile: '345-678-9012',
    dateReturned: '2002-03-20T14:45:00',
    elapsedK2K: 3,
    name: 'Sara White',
    comments: 'Replaced brake pads.'
  },
  {
    registration: 'JKL012',
    claimReference: 'CR792',
    manufacturer: 'BMW',
    model: 'X5',
    ownerName: 'David Black',
    ownerEmail: 'davidblack@example.com',
    ownerMobile: '456-789-0123',
    dateReturned: '2003-04-25T09:15:00',
    elapsedK2K: 10,
    name: 'Emma Wilson',
    comments: 'Engine diagnostics and repair.'
  },
  {
    registration: 'MNO345',
    claimReference: 'CR793',
    manufacturer: 'Audi',
    model: 'A4',
    ownerName: 'Linda Blue',
    ownerEmail: 'lindablue@example.com',
    ownerMobile: '567-890-1234',
    dateReturned: '2004-05-30T16:00:00',
    elapsedK2K: 8,
    name: 'James Taylor',
    comments: 'Transmission fluid changed.'
  },
  {
    registration: 'PQR678',
    claimReference: 'CR794',
    manufacturer: 'Mercedes',
    model: 'C-Class',
    ownerName: 'Robert Gray',
    ownerEmail: 'robertgray@example.com',
    ownerMobile: '678-901-2345',
    dateReturned: '2005-06-10T11:20:00',
    elapsedK2K: 6,
    name: 'Olivia Martin',
    comments: 'Repaired air conditioning system.'
  },
  {
    registration: 'STU901',
    claimReference: 'CR795',
    manufacturer: 'Nissan',
    model: 'Altima',
    ownerName: 'Patricia King',
    ownerEmail: 'patriciaking@example.com',
    ownerMobile: '789-012-3456',
    dateReturned: '2006-07-15T13:50:00',
    elapsedK2K: 4,
    name: 'William Scott',
    comments: 'Battery replaced.'
  },
  {
    registration: 'VWX234',
    claimReference: 'CR796',
    manufacturer: 'Hyundai',
    model: 'Elantra',
    ownerName: 'Barbara Lee',
    ownerEmail: 'barbaralee@example.com',
    ownerMobile: '890-123-4567',
    dateReturned: '2007-08-20T08:40:00',
    elapsedK2K: 9,
    name: 'Charles Young',
    comments: 'Tire rotation and alignment. Test message for long items. Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.'
  }
];

export function ZurichCSIReport({
  reportPath,
  setErrorMessage,
  setToast,
}: Readonly<ReportProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = new URLSearchParams(location.search);
  const urlParamsValue = {
    startDate: urlParams.get('startDate'),
    endDate: urlParams.get('endDate'),
  };
  const [startDate, setStartDate] = useState<Date | null>(
    convertToDate(urlParamsValue.startDate) ?? null
  );
  const [endDate, setEndDate] = useState<Date | null>(
    convertToDate(urlParamsValue.endDate) ?? null
  );

  const queryEnabled = !!urlParamsValue.startDate && !!urlParamsValue.endDate;

  const { isReportFetching, refetchReport, reportData } = useZurichCSIReport(
    urlParamsValue.startDate,
    urlParamsValue.endDate,
    queryEnabled,
    handleError
  );

  const rows = useMemo<Row>(() => reportData, [reportData]);
  //const rows = mockedReportData; // TODO: remove mocked data when testing isi done 

  useEffect(() => {
    navigateIfPossible();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParamsValue.startDate, urlParamsValue.endDate]);

  const reportHeaderTagLine = useMemo(
    () =>
      `CSI Triggers for delivered jobs between ${formatDate(startDate)} and ${formatDate(endDate)} on ${formatDate(new Date())}`,
    [startDate, endDate]
  );

  return (
    <MainPageContainer isLoading={isReportFetching} direction="column" wrap="nowrap">
      <UpperInfoContainer direction="column">
        <ReportBackButton />
        <ReportHeader
          reportName={ZURICH_CSI_REPORT}
          infoMessage={ZURICH_CSI_REPORT_MESSAGE}
        />
        <ReportSearchForm
          endDate={endDate}
          isDisabled={isReportFetching}
          startDate={startDate}
          setEndDate={setEndDate}
          setStartDate={setStartDate}
          handleSubmit={handleSubmit}
        />
      </UpperInfoContainer>

      {!isReportFetching && rows?.length === 0 && <NoReportDataMessage />}

      <ReportGridContainer direction="column">
        <ReportGridNew 
          columns={columns}
          displayGroupingPanel
          headerTitleForSheet={ZURICH_CSI_REPORT}
          isLoading={isReportFetching}
          pdfOrientation='l'
          reportHeaderTagLine={reportHeaderTagLine}
          rows={rows}
          title={ZURICH_CSI_REPORT_GRID_TITLE}
        />
      </ReportGridContainer>
    </MainPageContainer>
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (
      urlParamsValue.startDate == dateToString(startDate) &&
      urlParamsValue.endDate == dateToString(endDate)
    )
      refetchReport();
    else navigateIfPossible();
  }

  function navigateIfPossible() {
    if (!!startDate && !!endDate)
      navigate(
        createReportURL(
          {
            startDate: dateToString(startDate),
            endDate: dateToString(endDate),
          },
          reportPath,
        ), { replace: true });
  }

  function handleError(error: AxiosError) {
    reportLogger(error);
    setErrorMessage(errorMessages.reports('Zurich CSI Report'));
    setToast(true);
  }
}
